/* eslint sort-keys: "error" */
import * as _ from 'lodash-es';
import moment from 'moment';
import type { TypePolicy } from '@apollo/client/cache';
import { relayStylePagination } from '@apollo/client/utilities';
import type { TypedTypePolicies } from '../../graphql/generated/apolloTypedTypePolicies';
import baseTypePolicies from '../../graphql/generated/baseTypePolicies';

const { merge: listPostsMerge } = baseTypePolicies.Query.fields.listPosts;

const queryPolicy = {
  fields: {
    currentSession: { keyFields: false, merge: true },
    listPosts: {
      ...baseTypePolicies.Query.fields.listPosts,
      merge:
        // In practice this should always be a function, but TypeScript doesn't know that
        typeof listPostsMerge === 'function'
          ? (existing, incoming, options) => {
              const result = listPostsMerge(existing, incoming, options);
              return result?.edges
                ? {
                    ...result,
                    edges: _.uniqBy(result.edges, (edge) =>
                      options.readField('id', options.readField('node', edge)),
                    ),
                  }
                : result;
            }
          : listPostsMerge,
    },
    post(_value, { args, toReference }) {
      return toReference({
        __typename: 'Post',
        id: args!.id,
      });
    },
    publicSearch: relayStylePagination(),
    topic(_value, { args, toReference }) {
      return toReference({
        __typename: 'Topic',
        id: args!.id,
      });
    },
  },
} as TypePolicy;

const typeOverrides = {
  Activity: { keyFields: ['actedAt', 'actor'] },
  BillingPricing: { merge: true },
  Contribution: { keyFields: ['user', ['id']] },
  CurrentUser: { keyFields: [] },
  CustomDomain: { keyFields: ['hostname'] },
  Image: { keyFields: false, merge: true },
  IntegrationSettings: { keyFields: false, merge: true },
  MentionPreview: { merge: true },
  OrganizationSettings: { keyFields: false, merge: true },
  Post: {
    fields: {
      userViewedAt: {
        merge: (existing, incoming) => {
          if (!existing) return incoming;
          return moment(existing).isBefore(moment(incoming))
            ? incoming
            : existing;
        },
      },
    },
  },
  PostActivity: {
    keyFields: ['actedAt', 'action', 'actors', ['id'], 'source', ['id']],
  },
  Topic: {
    fields: {
      membership: { merge: true },
    },
  },
  TopicActivity: {
    keyFields: ['actedAt', 'action', 'actors', ['id'], 'source', ['id']],
  },
  User: { merge: true },
  UserSettings: { merge: true },
  UserSidebarSettings: { merge: true },
} as TypedTypePolicies;

const typePolicies: TypedTypePolicies = _.merge(
  baseTypePolicies,
  typeOverrides,
  { Query: queryPolicy },
);

export default typePolicies;
